import { BEGIN, REVERT } from 'redux-optimist'
import { v4 as uuidV4 } from 'uuid'
import config from 'config'
import * as types from 'constants/action_types'
import debug, { logError } from 'util/debug'
import { getUserAgent, isWebPushSupported } from 'util/browser'
import { isGranted, isDenied, askPermission } from 'util/webPush/permissions'
import { subscribeUserToPush } from 'util/webPush/subscriptions'
import { getMatrixEnvironment } from 'ducks/chat/utils/client'
import { doUpdateAudioNotifications } from 'ducks/chat/actions/notifications'

const {
  vapidPublicKey: VAPID_PUBLIC_KEY,
  sygnalServerUrl: SYGNAL_SERVER_URL,
} = config

const logUnsupported = () => {
  debug('This browser does not support web push notifications')
  if (bugsnagClient) {
    bugsnagClient.notify({
      name: 'WebPushCompatibilityError',
      message: 'web push not supported',
    })
  }
}

const doSubscribeUserToPush = vapidPublicKey => dispatch => {
  dispatch({
    type: types.WEB_PUSH_SUBSCRIPTION_CREATE,
    data: { vapidPublicKey },
  })

  return subscribeUserToPush(vapidPublicKey)
    .then(subscription => {
      dispatch({
        type: types.WEB_PUSH_SUBSCRIPTION_CREATED,
        data: { subscription },
      })
      return subscription
    })
    .catch(err => {
      dispatch({
        type: types.WEB_PUSH_SUBSCRIPTION_CREATE_FAIL,
        data: { err },
      })
    })
}

// Send a (granted) push subscription to the back end
const doSaveWebPushSubscription = subscriptionObj => async dispatch => {
  if (!subscriptionObj) return false
  const transactionID = uuidV4()
  const userAgent = getUserAgent()
  const {
    endpoint,
    keys: { p256dh, auth },
  } = subscriptionObj.toJSON()
  dispatch({
    type: types.SAVE_WEB_PUSH_SUBSCRIPTION_REQUEST,
    data: {
      endpoint,
      id: `optimist-${transactionID}`, // for debugging
    },
    optimist: { type: BEGIN, id: transactionID },
  })

  const handleError = err => {
    // destroy the subscription we just created so the SW isnt out of sync
    // with our API
    if (subscriptionObj) subscriptionObj.unsubscribe() // returns boolean
    dispatch({
      type: types.SAVE_WEB_PUSH_SUBSCRIPTION_FAIL,
      data: { err },
      optimist: { type: REVERT, id: transactionID },
    })
  }

  const client = await getMatrixEnvironment()
  try {
    await client.setPusher({
      lang: 'en-US',
      kind: 'http',
      app_display_name: 'GrooveHQ',
      device_display_name: userAgent,
      // profile_tag: 'xxyyzz',
      app_id: 'com.groovehq.app.web',
      pushkey: p256dh,
      data: {
        url: `${SYGNAL_SERVER_URL}/_matrix/push/v1/notify`,
        endpoint,
        auth,
        events_only: true,
      },
      append: false,
    })
  } catch (err) {
    return handleError(err)
  }

  return dispatch({
    type: types.SAVE_WEB_PUSH_SUBSCRIPTION_SUCCESS,
    data: { endpoint },
    // We need to revert the optimistic one since itll hang around in
    // byEndpoint otherwise
    optimist: { type: REVERT, id: transactionID },
  })
}

export const doAskPushNotificationPermission = () => {
  // the app shouldnt even be triggering this function if web push not
  // supported, but we check if push is supported just in case
  if (!isWebPushSupported()) {
    logUnsupported()
    return Promise.reject(new Error('unsupported'))
  }
  if (isDenied()) return Promise.reject(new Error('denied'))
  if (isGranted()) return Promise.resolve() // already granted
  // At this point the Notification Permission  must be 'default' (i.e.
  // unknown) so ask user
  return askPermission()
}

// Fetches existing Push Sub (or registers a new one), and saves it
export const doSubscribe = () => dispatch => {
  const transactionID = uuidV4()
  dispatch({
    type: types.WEB_PUSH_SUBSCRIBE,
    // create a dummy sub optimistically
    data: {
      endpoint: `optimist-${transactionID}`,
      userAgent: getUserAgent(),
    },
    optimist: { type: BEGIN, id: transactionID },
  })

  return doAskPushNotificationPermission()
    .then(() => dispatch(doSubscribeUserToPush(VAPID_PUBLIC_KEY)))
    .then(webPushSubscription =>
      dispatch(doSaveWebPushSubscription(webPushSubscription))
    )
    .catch(reason => {
      logError('Web Push Subscribing failed', { reason })
      dispatch({
        type: types.WEB_PUSH_SUBSCRIBE_FAIL,
        data: { reason },
        optimist: { type: REVERT, id: transactionID },
      })
      dispatch(doUpdateAudioNotifications(false))
      if (bugsnagClient) bugsnagClient.notify(reason)
    })
    .then(() =>
      dispatch({
        type: types.WEB_PUSH_SUBSCRIBE_COMPLETE,
        // Throw away the dummy sub either way
        optimist: { type: REVERT, id: transactionID },
      })
    )
}
